import React from 'react'
import { Popover, Button, Checkbox, Input } from 'antd'
import _ from 'lodash'
import { observer } from 'mobx-react'
import { computed, observable } from 'mobx'
import { translateKey as t } from '../../../locales/translation'
import { List } from 'react-virtualized'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFilter } from '@fortawesome/free-solid-svg-icons'

/**
 * @tutorial structure
 * Columns Filter structure example:

  Filter: props => (
    <MultiselectFilter
      {...props}
      values={[
        { title: '1315', value: '1315' },
        { title: '1320', value: '1320' }
      ]}

      or for dynamic values use dynamicValues instead of values:
      dynamicValues={() => func returning same array structure as the values}
    />
  )

  * If this Filter is used, we need to include in the column constructor filter value like so:

  filter: msFilterMethods

 */
// Types for the props
@observer
export class MultiselectFilter extends React.PureComponent<
  {
    column: any
    values?: any
    dynamicValues?: Function
    handleFilter?: any
  }
> {
  @observable private values = this.props.dynamicValues ? this.props.dynamicValues() : this.props.values
  @observable private isMenuShowing = false
  @observable private searchVal = ''

  @computed get validValues() {
    return this.values.filter((v) => {
      if (this.searchVal && v.title) {
        return v.title.toLowerCase().includes(this.searchVal.toLowerCase().trim())
      }

      return true
    })
  }

  private handleOnChange = (checked, value) => {
    const { column } = this.props

    let colValues = column.filterValue || {}

    if (colValues) {
      if (colValues[value]) {
        delete colValues[value]
      } else {
        colValues[value] = checked
      }
    } else {
      colValues[value] = checked
    }

    this.handleValueChange(column, colValues)
  }

  private handleValueChange = (column, colValues) => {
    column.filterValue = colValues && Object.values(colValues).length > 0 ? colValues : ''

    if (!column.filterOnSubmit) {
      column.finalFilterValue = colValues && Object.values(colValues).length > 0 ? colValues : ''
    }
  }

  private getValue = (value) => {
    const { column } = this.props

    if (column.filterValue && column.filterValue[value]) {
      return column.filterValue[value]
    } else {
      return undefined
    }
  }

  private renderValues = (props) => {
    const item = this.validValues[props.index]

    return (
      <div key={props.key} style={props.style}>
        <Checkbox
          data-e2e={'multiselect-' + props.index}
          onChange={(e) => this.handleOnChange(e.target.checked, item.value)}
          checked={this.getValue(item.value)}
          style={{ marginLeft: 0, padding: '0 15px', width: '100%' }}
          className='truncate'
        >
          {item.title}
        </Checkbox>
      </div>
    )
  }

  private renderResetBtn = (column) => {
    const handleAllOnChange = (checked) => {
      let colValues = {}

      if (checked) {
        this.validValues.forEach((v) => {
          colValues[v.value] = checked
        })
      }

      if (colValues && Object.values(colValues).length > 0) {
        this.handleValueChange(column, colValues)
      } else {
        column.filterValue = ''
        column.finalFilterValue = ''
      }
    }

    const getValue = () => {
      if (column.filterValue && Object.values(column.filterValue).length === this.validValues.length) {
        return true
      }

      return false
    }

    const getIndeterminate = () => {
      if (column.filterValue) {
        if (Object.values(column.filterValue).length !== this.validValues.length) return true
      } else {
        return false
      }
    }

    return (
      <div>
        <Checkbox
          onChange={(e) => handleAllOnChange(e.target.checked)}
          checked={getValue()}
          indeterminate={getIndeterminate()}
          style={{ marginRight: 8 }}
        />
        <Button
          size='small'
          disabled={!column.filterValue}
          onClick={() => {
            column.filterValue = ''
            this.resetSearch(null)
            this.props.handleFilter(column, '')
          }}
        >
          {t('lblReset', 'Reset')}
        </Button>
      </div>
    )
  }

  private resetSearch = (value) => {
    if (!value) {
      this.searchVal = ''
    }
  }

  private renderContent = (column) => {
    return (
      <div>
        <Input
          data-e2e='multiselect-search-filter'
          size='small'
          style={{ marginBottom: 23 }}
          placeholder={t('lblSearch', 'Search')}
          value={this.searchVal}
          onChange={(e) => (this.searchVal = e.currentTarget.value)}
        />
        <List
          width={200}
          style={{ margin: '-12px -16px', borderTop: '1px solid #e8e8e8', paddingTop: 5 }}
          height={this.getListHeight()}
          rowCount={this.validValues.length}
          rowHeight={23}
          rowRenderer={this.renderValues}
          noRowsRenderer={() => this.noContentRenderer()}
        />
        {column.filterOnSubmit && this.validValues.length > 0 && (
          <div style={{ marginTop: 12, textAlign: 'right' }}>
            <Button
              size='small'
              type='primary'
              disabled={!column.filterValue && !column.finalFilterValue}
              onClick={() => {
                this.props.handleFilter(column, 'multiSelect')
                this.isMenuShowing = false
              }}
            >
              {t('lblApply', 'Apply')}
            </Button>
          </div>
        )}
      </div>
    )
  }

  private getListHeight = () => {
    if (this.validValues.length === 0) {
      return 33
    }

    if (this.validValues.length > 15) {
      return 285
    }

    return this.validValues.length * 23 + 10
  }

  private noContentRenderer = () => {
    return <div className='text-center'>{t('lblNoResultsFound', 'No results found')}</div>
  }

  public render() {
    const { column, dynamicValues } = this.props

    return (
      <Popover
        placement='bottom'
        content={this.renderContent(column)}
        trigger='click'
        title={this.renderResetBtn(column)}
        onOpenChange={(val) => {
          this.isMenuShowing = val
          this.resetSearch(val)
        }}
        open={this.isMenuShowing}
      >
        <Button size='small' style={{ width: '100%' }} onClick={(e) => (dynamicValues ? (this.values = dynamicValues()) : e.preventDefault())}>
          {column.filterValue ? <FontAwesomeIcon icon={faFilter} style={{ color: "#1890ff" }} /> : <FontAwesomeIcon icon={faFilter} />}
        </Button>
      </Popover>
    )
  }
}

function FilteringMethod(value, query) {
  return query[value]
}

export { FilteringMethod }
